import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { movieList, tvList, animeList, varietyList } from '@/mocks/category';

const CATEGORY_CONFIG: Record<
  string,
  {
    name: string;
    icon: string;
    description: string;
    data: typeof movieList;
    seoDesc: string;
  }
> = {
  movie: {
    name: '电影',
    icon: 'ri-film-line',
    description: '海量高清电影资源，涵盖动作、喜剧、科幻、悬疑、爱情等多种类型，实时更新最新大片。',
    data: movieList,
    seoDesc: '91影视电影频道提供海量高清电影在线免费观看，涵盖动作、喜剧、科幻、悬疑、爱情等多种类型，实时更新最新大片，无需注册即可畅享。',
  },
  tv: {
    name: '电视剧',
    icon: 'ri-tv-line',
    description: '热门电视剧全集免费在线观看，国产剧、美剧、韩剧、日剧一网打尽，追剧首选平台。',
    data: tvList,
    seoDesc: '91影视电视剧频道提供热门电视剧全集免费在线观看，国产剧、美剧、韩剧、日剧一网打尽，更新及时，高清流畅。',
  },
  anime: {
    name: '动漫',
    icon: 'ri-paint-brush-line',
    description: '最新最热动漫连载免费观看，国漫、日漫、欧美动漫精彩不断，二次元爱好者必备。',
    data: animeList,
    seoDesc: '91影视动漫频道提供最新最热动漫连载免费在线观看，国漫、日漫、欧美动漫精彩不断，高清画质，更新及时。',
  },
  variety: {
    name: '综艺',
    icon: 'ri-movie-line',
    description: '热门综艺节目在线观看，真人秀、脱口秀、音乐竞演应有尽有，周末娱乐首选。',
    data: varietyList,
    seoDesc: '91影视综艺频道提供热门综艺节目在线免费观看，真人秀、脱口秀、音乐竞演应有尽有，实时更新最新节目。',
  },
};

const TABS = [
  { key: 'movie', label: '电影', icon: 'ri-film-line' },
  { key: 'tv', label: '电视剧', icon: 'ri-tv-line' },
  { key: 'anime', label: '动漫', icon: 'ri-paint-brush-line' },
  { key: 'variety', label: '综艺', icon: 'ri-movie-line' },
];

export default function CategoryPage() {
  const { type = 'movie' } = useParams<{ type: string }>();
  const [activeTab, setActiveTab] = useState(type);
  const [sortBy, setSortBy] = useState<'hot' | 'new' | 'rating'>('hot');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    setActiveTab(type);
  }, [type]);

  const config = CATEGORY_CONFIG[activeTab] || CATEGORY_CONFIG.movie;
  let displayData = [...config.data];

  if (sortBy === 'new') {
    displayData.sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime());
  } else if (sortBy === 'rating') {
    displayData.sort((a, b) => b.rating - a.rating);
  } else {
    displayData.sort((a, b) => (b.isHot ? 1 : 0) - (a.isHot ? 1 : 0) || b.rating - a.rating);
  }

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `91影视${config.name}频道 - ${config.name}在线免费观看`,
    description: config.seoDesc,
    url: `https://www.91yingshi.com/category/${activeTab}`,
    isPartOf: { '@type': 'WebSite', name: '91影视', url: 'https://www.91yingshi.com' },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.91yingshi.com' },
      {
        '@type': 'ListItem',
        position: 2,
        name: config.name,
        item: `https://www.91yingshi.com/category/${activeTab}`,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${config.name}频道`}
        description={config.seoDesc}
        keywords={`91影视${config.name},91影视${config.name}在线观看,91影视免费${config.name},91影视最新${config.name}`}
        canonicalUrl={`https://www.91yingshi.com/category/${activeTab}`}
        schema={{ ...schema, ...breadcrumbSchema } as Record<string, unknown>}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="bg-background-100/50 border-b border-background-200/50">
            <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4">
                <Link to="/" className="hover:text-foreground-200 transition-colors cursor-pointer">
                  首页
                </Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-300">{config.name}</span>
              </nav>

              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary-500/10 flex items-center justify-center">
                    <i className={`${config.icon} text-xl text-primary-400`}></i>
                  </div>
                  <div>
                    <h1 className="text-lg md:text-xl font-bold text-foreground-50 font-heading">
                      {config.name}
                    </h1>
                    <p className="text-xs text-foreground-500 mt-0.5">{config.description}</p>
                  </div>
                </div>

                <div className="md:ml-auto relative">
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-background-100 border border-background-200/50 text-sm text-foreground-300 hover:text-foreground-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-sort-desc text-sm"></i>
                    {sortBy === 'hot' ? '热门排序' : sortBy === 'new' ? '最新更新' : '评分最高'}
                    <i className="ri-arrow-down-s-line text-xs"></i>
                  </button>
                  {isDropdownOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setIsDropdownOpen(false)}></div>
                      <div className="absolute right-0 top-full mt-1 w-36 bg-background-100 border border-background-200/50 rounded-lg shadow-lg z-20 overflow-hidden">
                        {[
                          { key: 'hot', label: '热门排序' },
                          { key: 'new', label: '最新更新' },
                          { key: 'rating', label: '评分最高' },
                        ].map((opt) => (
                          <button
                            key={opt.key}
                            onClick={() => {
                              setSortBy(opt.key as 'hot' | 'new' | 'rating');
                              setIsDropdownOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2.5 text-sm hover:bg-background-200/50 transition-colors cursor-pointer whitespace-nowrap ${
                              sortBy === opt.key ? 'text-primary-400' : 'text-foreground-300'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {TABS.map((tab) => (
                <Link
                  key={tab.key}
                  to={`/category/${tab.key}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeTab === tab.key
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-400 hover:text-foreground-200 hover:bg-background-200'
                  }`}
                >
                  <i className={`${tab.icon} text-sm`}></i>
                  {tab.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 pb-16">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {displayData.map((item) => (
                <Link
                  key={item.id}
                  to={`/article/${item.id}`}
                  className="group relative block cursor-pointer"
                >
                  <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-background-100">
                    <img
                      src={item.poster}
                      alt={`${item.title} 海报`}
                      title={`${item.title} - 91影视${config.name}`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-0.5 rounded-md bg-primary-500 text-white text-xs font-medium">
                        {item.rating}
                      </span>
                    </div>

                    {item.isHot && (
                      <div className="absolute top-2 right-2">
                        <span className="px-2 py-0.5 rounded-md bg-accent-500 text-black text-xs font-bold">
                          HOT
                        </span>
                      </div>
                    )}

                    <div className="absolute bottom-0 left-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <p className="text-xs text-foreground-300 line-clamp-2 mb-2">{item.description}</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-foreground-400">{item.year}</span>
                        <span className="text-xs text-foreground-600">·</span>
                        <span className="text-xs text-foreground-400">{item.genre}</span>
                        <span className="text-xs text-foreground-600">·</span>
                        <span className="text-xs text-foreground-400">{item.duration}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-2.5">
                    <h3 className="text-sm font-medium text-foreground-100 group-hover:text-primary-400 transition-colors line-clamp-1">
                      {item.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-0.5">{item.updateTime}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}